import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RacingLeadDialog({ open, onOpenChange }: Props) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [dream, setDream] = useState("");
  const [loading, setLoading] = useState(false);

  const valid = () => {
    if (name.trim().length < 2) return "Įveskite vardą";
    const c = contact.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c);
    const digits = c.replace(/\D/g, "");
    if (!isEmail && digits.length < 7) return "Įveskite el. paštą arba telefono numerį";
    if (dream.trim().length < 5) return "Papasakok savo svajonę";
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = valid();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("send-racing-lead", {
        body: { name: name.trim(), contact: contact.trim(), dream: dream.trim() },
      });
      if (error) throw error;
      toast.success("Ačiū! Susisieksime artimiausiu metu.");
      setName(""); setContact(""); setDream("");
      onOpenChange(false);
    } catch {
      toast.error("Nepavyko išsiųsti. Pabandykite dar kartą.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-sans font-black uppercase italic text-2xl">
            Pradėk <span className="text-primary">savo kelią</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Palik kontaktus ir papasakok, kodėl ralis. Susisieksime asmeniškai.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="r-name" className="text-xs tracking-[0.2em] uppercase text-white/60">Vardas</Label>
            <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-white/10 text-white" placeholder="Tavo vardas" maxLength={120} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-contact" className="text-xs tracking-[0.2em] uppercase text-white/60">Telefonas arba el. paštas</Label>
            <Input id="r-contact" value={contact} onChange={(e) => setContact(e.target.value)}
              className="bg-white/5 border-white/10 text-white" placeholder="+370... arba vardas@email.lt" maxLength={200} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-dream" className="text-xs tracking-[0.2em] uppercase text-white/60">Kodėl ralis? Papasakok savo svajonę</Label>
            <Textarea id="r-dream" value={dream} onChange={(e) => setDream(e.target.value)}
              className="bg-white/5 border-white/10 text-white min-h-[120px]"
              placeholder="Apie ką svajoji – pirmas startas, podiumas, technika, pace notes..." maxLength={2000} />
          </div>
          <button type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Siunčiama</> : <>Siųsti <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}